import moment from 'moment';
import camelCase from 'lodash/camelCase';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Form, FormContext, Select, useScreenResolution } from 'dash-ui';

import {
  ProjectStatusesEnum,
  useGetAmenitiesQuery,
} from 'gql/generated.graphql';

import { FormFields } from 'containers/alerts/AddAlertModal/constants/constants';

const AdditionalPropertyOptionsSection = () => {
  const { t } = useTranslation();
  const { stateHandlers, formState } = useContext(FormContext);
  const startYear = formState?.yearBuilt?.min;
  const endYear = formState?.yearBuilt?.max;
  const startYearAsDate = startYear && moment([startYear, 1, 1]).toDate();
  const endYearAsDate = endYear && moment([endYear, 1, 1]).toDate();
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const { data, loading } = useGetAmenitiesQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        isPrimary: true,
      },
      pagination: { allowLimit: false },
    },
  });

  const options = useMemo(() => {
    const uniqAmenities = data?.getAmenities.collection || [];

    return uniqAmenities.map((item) => ({
      value: item._id,
      label: item.name,
    }));
  }, [data]);

  return (
    <>
      <Form.Group mt={4}>
        <Form.Label md>{t('common:labels.featuresAndAmenities')}</Form.Label>
        <Select
          w="100%"
          isMulti
          isSearchable
          // disablePortalTarget={!isMobile}
          value={formState[FormFields.FeaturesAndAmenities]}
          onChanged={(_, values) =>
            stateHandlers[FormFields.FeaturesAndAmenities](values)
          }
          isLoading={loading}
          options={options}
          placeholder={t('common:placeholders.selectOption')}
        />
      </Form.Group>
      <Flex
        mt={4}
        w="100%"
        alignItems="center"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Form.Group
          w="100%"
          mr={[0, 2, 2, 2]}
          maxWidth={['100%', 214, 214, 214]}
        >
          <Form.Label md>{t('forms:labels.projectStatus')}</Form.Label>
          <Form.Select
            w="100%"
            isMulti
            isSearchable={false}
            // disablePortalTarget={!isMobile}
            options={Object.values(ProjectStatusesEnum).map((status) => ({
              label: t(`forms:labels.projectStatuses.${camelCase(status)}`),
              value: status,
            }))}
            value={formState[FormFields.ProjectStatus]}
            placeholder={t('common:placeholders.selectProjectStatus')}
            onChanged={(_: string, values?: string[]) =>
              stateHandlers[FormFields.ProjectStatus](values)
            }
          />
        </Form.Group>
        <Form.Group
          maxWidth={['100%', 155, 155, 155]}
          w="100%"
          mt={[4, 0, 0, 0]}
        >
          <Form.Label md>{t('pages:propertyPage.yearBuilt')}</Form.Label>
          <Form.DateRangePicker
            showYearPicker
            dateFormat="yyyy"
            startDate={startYearAsDate}
            endDate={endYearAsDate}
            onChange={({ startDate, endDate }) =>
              stateHandlers[FormFields.YearBuilt]({
                min: startDate.getFullYear(),
                max: endDate.getFullYear(),
              })
            }
            onClear={() => stateHandlers[FormFields.YearBuilt](null)}
            placeholderText="yyyy - yyyy"
          />
        </Form.Group>
      </Flex>
    </>
  );
};

export default AdditionalPropertyOptionsSection;
